import { AlertTriangle, CheckCircle2, Wrench, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { EchoflicksSessionFramingEvent } from "../../types";
import {
  FRAMING_PRIORITY_TIER_LABELS,
  type PrioritizedFramingScene,
} from "./framingScenePriority";

/**
 * Compact, metadata-only scene row (CROP-WB-005). Never contains signed
 * URLs, source object keys, raw names, prompts, or private thumbnails.
 */

function sceneInterval(
  events: readonly EchoflicksSessionFramingEvent[],
): { start: number; end: number } | null {
  for (const event of events) {
    const payload = event.payload ?? {};
    const start =
      event.timeline_start_seconds ?? payload.timeline_start_seconds;
    const end = event.timeline_end_seconds ?? payload.timeline_end_seconds;
    if (
      typeof start === "number" &&
      typeof end === "number" &&
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      end > start
    ) {
      return { start, end };
    }
  }
  return null;
}

function formatSeconds(value: number): string {
  const minutes = Math.floor(value / 60);
  const seconds = value - minutes * 60;
  return `${minutes}:${seconds.toFixed(1).padStart(4, "0")}`;
}

function qcIcon(status?: string | null) {
  if (status === "failed")
    return <XCircle className="size-4 text-destructive" />;
  if (status === "warning")
    return <AlertTriangle className="size-4 text-amber-500" />;
  if (status === "passed")
    return <CheckCircle2 className="size-4 text-emerald-500" />;
  return <AlertTriangle className="size-4 text-muted-foreground" />;
}

export function FramingSceneRow({
  entry,
  onInspect,
}: {
  entry: PrioritizedFramingScene;
  onInspect: (sceneId: string) => void;
}) {
  const { scene, tier, reasons, feedbackStatus } = entry;
  const interval = sceneInterval(scene.events);
  const kinds = [
    ...new Set(
      scene.subjects
        .map((subject) => subject.kind)
        .filter((kind): kind is string => Boolean(kind)),
    ),
  ];
  const aspectChain = [scene.providerAspect, scene.deliveryAspect]
    .filter(Boolean)
    .join(" → ");
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
      data-testid={`framing-scene-row-${scene.sceneId}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        {qcIcon(scene.qc?.status)}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Scene {scene.sceneId}</span>
            {interval ? (
              <span className="font-mono text-xs text-muted-foreground">
                {formatSeconds(interval.start)}–{formatSeconds(interval.end)}
              </span>
            ) : null}
            {feedbackStatus ? (
              <Badge
                variant={
                  feedbackStatus === "exact" ? "destructive" : "secondary"
                }
              >
                feedback: {feedbackStatus}
              </Badge>
            ) : null}
            {tier !== "timeline" ? (
              <Badge variant="outline">
                {FRAMING_PRIORITY_TIER_LABELS[tier]}
              </Badge>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {kinds.length > 0 ? <span>{kinds.join(", ")}</span> : null}
            {aspectChain ? (
              <span className="font-mono">{aspectChain}</span>
            ) : null}
            {scene.strategy ? (
              <span>{scene.strategy.replaceAll("_", " ")}</span>
            ) : null}
            {scene.repaired ? (
              <span className="inline-flex items-center gap-1">
                <Wrench className="size-3" /> repaired
              </span>
            ) : null}
            {scene.fallback ? <span>fallback</span> : null}
            <span>
              attempt {scene.attemptId ?? "legacy"}
              {scene.attemptCount > 1
                ? ` · ${scene.attemptCount} attempts`
                : ""}
            </span>
          </div>
          {reasons.length > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {reasons.slice(0, 2).join(" · ")}
            </p>
          ) : null}
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onInspect(scene.sceneId)}
      >
        Inspect crop pixels
      </Button>
    </div>
  );
}
